/* eslint-disable react/jsx-closing-tag-location */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { flattenDeep, get } from 'lodash';
import { SubDrawerData, DrawerFunctions, SubDrawerItem } from '../../types/Drawer';
import getTooltipForDisabled, { isItemDisabled, isItemHidden, disabledFunction } from './utils';

function SubDrawer({
  drawerFunctions,
  subDrawerData,
  activePage,
  hiddenNavKeys,
  disabledNavKeys,
}: {
      drawerFunctions: DrawerFunctions;
      subDrawerData: SubDrawerData;
      activePage: string;
      hiddenNavKeys: string[];
      disabledNavKeys: string[];
    }) {
  const [activeMenuItem, setActiveMenuItem] = useState('');
  const returnActiveClass = (key: string) => (activeMenuItem === key ? 'active' : '');

  const itemOnClickGen = (item: SubDrawerItem) => {
    if (isItemDisabled(item, disabledNavKeys)) {
      return disabledFunction;
    }
    const onClickFunc = get(drawerFunctions, `${item.onClickFunctionName}`, disabledFunction);
    const onClick = () => {
      if (!item.external) setActiveMenuItem(item.key);
      onClickFunc();
    };
    return onClick;
  };

  const findActiveItem = (activeItemKey: string) => {
    const activeGroup = subDrawerData.groups.find((group) => group.items.some(
      (item) => item.key === activeItemKey,
    ));
    if (activeGroup) {
      const activeItem = activeGroup.items.find(
        (item) => item.key === activeItemKey,
      );
      if (activeItem) return activeItem;
    }
    return null;
  };

  useEffect(() => {
    if (subDrawerData && subDrawerData.groups.length > 0) {
      const defaultKey = subDrawerData.defaultTab;
      const subDrawerItems = flattenDeep(subDrawerData.groups.map(({ items }) => items));
      const pageIsOfSameSubDrawer = subDrawerItems.find(({ key }) => key === activePage);
      const activeKey = pageIsOfSameSubDrawer && activePage && activePage !== defaultKey
        ? activePage
        : defaultKey;
      if (activeKey !== activeMenuItem) {
        const activeItem = findActiveItem(activeKey);
        if (activeItem) {
          itemOnClickGen(activeItem)();
        }
      }
    }
  }, [subDrawerData]);

  useEffect(() => {
    if (activePage && subDrawerData && subDrawerData.groups.length > 0) {
      const activeItem = findActiveItem(activePage);
      // Dont click if already active
      if (activeItem && activeItem.key !== activeMenuItem) itemOnClickGen(activeItem)();
    }
  }, [activePage]);

  if (subDrawerData && subDrawerData.groups.length > 0) {
    return (
      <div className="subdrawer__container">
        <div className="subdrawer__header">{subDrawerData.title}</div>
        {subDrawerData.groups.map((group) => {
          // Remove hidden items
          let itemList = group.items.filter((item) => !isItemHidden(item, hiddenNavKeys));

          itemList = itemList.map((item) => {
            const onClick = itemOnClickGen(item);
            return { ...item, onClick };
          });
          // Do not return group if list is empty
          if (!itemList.length) return '';
          const { title, id } = group;
          return (
            <div key={id} className="subdrawer__group" id={id}>
              {title && <div className="subdrawer__group_header">{title}</div>}
              <div className="subdrawer__items_container">
                {itemList.map((item) => {
                  const {
                    key, text, onClick, icon, id: itemId,
                  } = item;
                  const disabled = isItemDisabled(item, disabledNavKeys);
                  return (
                    <>
                      {getTooltipForDisabled(<div
                        aria-hidden
                        key={itemId}
                        id={itemId}
                        onClick={onClick}
                        role="button"
                        className={`${returnActiveClass(key)} ${
                          disabled && 'disabled'
                        }`}
                      >
                        {icon}
                        <p>
                          {text}
                        </p>
                      </div>, disabled)}
                    </>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return null;
}

SubDrawer.defaultProps = {
  hiddenNavKeys: [],
  disabledNavKeys: [],
};

SubDrawer.propTypes = {
  drawerFunctions: PropTypes.object.isRequired,
  subDrawerData: PropTypes.object.isRequired,
  activePage: PropTypes.string.isRequired,
  hiddenNavKeys: PropTypes.array,
  disabledNavKeys: PropTypes.array,
};

export default SubDrawer;
